import React, { useState, useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { getSampleDataByTemplate, downloadCSV } from '../utils/sampleDataGenerator';

function DataRepository({ showToast }) {
  const { t } = useTranslation();
  const { dataSources, addDataSource, updateDataSource, deleteDataSource, sourceDefinitions } = useData();
  
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingData, setEditingData] = useState(null);

  // Merge source definitions with uploaded files
  const dataRepositories = useMemo(() => {
    const uploadedFiles = dataSources.filter(ds => ds.type === 'CSV' || ds.type === 'Excel');
    
    // Start with all source definitions
    const repositories = sourceDefinitions.map(sourceDef => {
      // Find uploaded files for this source
      const sourceFiles = uploadedFiles.filter(file => file.sourceName === sourceDef.name || file.sourceName === sourceDef.code);
      
      const repo = {
        id: sourceDef.id,
        code: sourceDef.code,
        sourceName: sourceDef.name,
        displayName: sourceDef.name,
        description: sourceDef.description,
        type: sourceDef.type,
        hasData: sourceFiles.length > 0,
        files: sourceFiles,
        tables: [],
        totalRows: 0,
        totalColumns: 0,
        fileCount: sourceFiles.length,
        lastUpdate: null,
        templateFiles: sourceDef.templateFiles || []
      };
      
      // If has uploaded files, process tables
      if (sourceFiles.length > 0) {
        sourceFiles.forEach(file => {
          if (file.data?.sheets) {
            file.data.sheets.forEach(sheet => {
              const tableId = sheet.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
              const existingTableIndex = repo.tables.findIndex(t => t.id === tableId);
              
              const tableData = {
                id: tableId,
                name: sheet.name,
                displayName: sheet.name,
                sourceFile: file.name,
                sourceFileId: file.id,
                headers: sheet.headers,
                data: sheet.data,
                rowCount: sheet.data.length,
                columnCount: sheet.headers.length,
                uploadDate: file.uploadDate
              };
              
              if (existingTableIndex >= 0) {
                // Replace with newer version
                if (new Date(file.uploadDate) > new Date(repo.tables[existingTableIndex].uploadDate)) {
                  repo.tables[existingTableIndex] = tableData;
                }
              } else {
                repo.tables.push(tableData);
              }
            });
          }
        });
        
        // Update totals
        repo.totalRows = repo.tables.reduce((sum, t) => sum + t.rowCount, 0);
        repo.totalColumns = Math.max(...repo.tables.map(t => t.columnCount), 0);
        repo.lastUpdate = sourceFiles.reduce((latest, f) => {
          return new Date(f.uploadDate) > new Date(latest || 0) ? f.uploadDate : latest;
        }, null);
      }
      
      return repo;
    });
    
    // Sort: sources with data first, then by name
    return repositories.sort((a, b) => {
      if (a.hasData !== b.hasData) {
        return a.hasData ? -1 : 1;
      }
      return a.sourceName.localeCompare(b.sourceName);
    });
  }, [dataSources, sourceDefinitions]);

  // Handle view data source
  const handleViewSource = (source) => {
    setSelectedSource(source);
    setSelectedTable(null);
  };

  // Handle view table
  const handleViewTable = (table) => {
    setSelectedTable(table);
  };

  // Handle edit table
  const handleEditTable = (table) => {
    setEditingData({
      table: table,
      headers: table.headers,
      data: JSON.parse(JSON.stringify(table.data)) // Deep clone
    });
    setShowEditModal(true);
  };

  // Handle save edited data
  const handleSaveEdit = () => {
    if (!editingData) return;

    // Convert grid data back to object format
    const updatedData = editingData.data.map(row => {
      const obj = {};
      editingData.headers.forEach((header, index) => {
        obj[header] = row[header];
      });
      return obj;
    });

    // Find the source file and update it
    const sourceFile = dataSources.find(ds => ds.id === editingData.table.sourceFileId);
    if (!sourceFile) {
      showToast(t('common.error'), 'error');
      return;
    }

    // Update the sheet data
    const updatedSheetData = {
      ...sourceFile.data,
      sheets: sourceFile.data.sheets.map(sheet => {
        if (sheet.name === editingData.table.name) {
          return {
            ...sheet,
            data: updatedData
          };
        }
        return sheet;
      })
    };

    // Update data source
    updateDataSource(sourceFile.id, {
      data: updatedSheetData,
      rowCount: updatedSheetData.sheets.reduce((sum, s) => sum + s.data.length, 0),
      lastSync: new Date().toLocaleString('vi-VN')
    });

    showToast(t('dataRepository.updateSuccess'), 'success');
    setShowEditModal(false);
    setEditingData(null);
    
    // Refresh selected source
    if (selectedSource) {
      const updatedRepo = dataRepositories.find(r => r.name === selectedSource.name);
      if (updatedRepo) {
        setSelectedSource(updatedRepo);
        const updatedTable = updatedRepo.tables.find(t => t.id === editingData.table.id);
        if (updatedTable) {
          setSelectedTable(updatedTable);
        }
      }
    }
  };

  // Handle delete data source
  const handleDeleteSource = (source) => {
    if (!window.confirm(t('dataRepository.deleteConfirm', { name: source.displayName, count: source.files.length }))) {
      return;
    }

    // Delete all files in this source
    source.files.forEach(file => {
      deleteDataSource(file.id);
    });

    showToast(t('dataRepository.deleteSuccess', { name: source.displayName }), 'success');
    setSelectedSource(null);
    setSelectedTable(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('vi-VN');
  };

  // Infer field type
  const inferFieldType = (value) => {
    if (value === null || value === undefined) return 'text';
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date';
    return 'text';
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="page-header-sticky">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-database-fill-gear me-2"></i>
            {t('dataRepository.title')}
          </h2>
          <p className="text-muted mb-0">
            {t('dataRepository.subtitle')}
          </p>
        </div>
      </div>

      <div className="row">
        {/* Left: Data Sources List */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-gradient-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-folder2-open me-2"></i>
                {t('dataRepository.dataSources')} ({dataRepositories.length})
              </h5>
            </div>
            <div className="card-body p-0">
              {dataRepositories.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-inbox fs-1 text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                  <p className="text-muted">{t('common.noData')}</p>
                  <a href="/data-upload" className="btn btn-sm btn-primary">
                    <i className="bi bi-cloud-upload me-1"></i>
                    {t('dataUpload.uploadFiles')}
                  </a>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {dataRepositories.map((source, index) => (
                    <div
                      key={index}
                      className={`list-group-item list-group-item-action ${selectedSource?.id === source.id ? 'active' : ''}`}
                      onClick={() => handleViewSource(source)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <i className={`bi ${source.hasData ? 'bi-folder-fill text-success' : 'bi-folder text-secondary'} me-2`}></i>
                            <strong>{source.sourceName}</strong>
                            <span className={`badge ms-2 ${source.hasData ? 'bg-success' : 'bg-secondary'}`}>
                              {source.hasData ? 'Đã có dữ liệu' : 'Chưa có dữ liệu'}
                            </span>
                          </div>
                          <div className="small text-muted ms-4 mb-2">
                            {source.description}
                          </div>
                          {source.hasData ? (
                            <>
                              <div className="small ms-4">
                                <span className="badge bg-secondary me-1">
                                  {source.tables.length} bảng
                                </span>
                                <span className="badge bg-info me-1">
                                  {source.totalRows.toLocaleString()} dòng
                                </span>
                                <span className="badge bg-warning text-dark">
                                  {source.fileCount} files
                                </span>
                              </div>
                              <div className="small text-muted mt-1 ms-4">
                                <i className="bi bi-clock me-1"></i>
                                Cập nhật: {formatDate(source.lastUpdate)}
                              </div>
                            </>
                          ) : (
                            <div className="small text-muted ms-4">
                              <i className="bi bi-info-circle me-1"></i>
                              {source.templateFiles && source.templateFiles.length > 0 ? 
                                `${source.templateFiles.length} template có sẵn` : 
                                'Chưa có dữ liệu'
                              }
                            </div>
                          )}
                        </div>
                        {source.hasData && (
                          <button
                            className={`btn btn-sm ${selectedSource?.id === source.id ? 'btn-light' : 'btn-outline-danger'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSource(source);
                            }}
                            title={t('common.delete')}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Tables and Data View */}
        <div className="col-md-8">
          {!selectedSource ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="bi bi-arrow-left-circle fs-1 text-muted mb-3" style={{ fontSize: '4rem' }}></i>
                <h5 className="text-muted">{t('dataRepository.subtitle')}</h5>
              </div>
            </div>
          ) : (
            <>
              {/* If no data, show templates */}
              {!selectedSource.hasData ? (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">
                      <i className="bi bi-file-earmark-text me-2"></i>
                      {selectedSource.displayName} - Chưa có dữ liệu
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="alert alert-info mb-4">
                      <i className="bi bi-info-circle me-2"></i>
                      Nguồn dữ liệu này chưa có dữ liệu được upload. Vui lòng tải lên file dữ liệu từ trang <strong>Tải dữ liệu lên</strong>.
                    </div>
                    
                    {selectedSource.templateFiles && selectedSource.templateFiles.length > 0 && (
                      <>
                        <h6 className="mb-3">
                          <i className="bi bi-download me-2"></i>
                          Templates có sẵn
                        </h6>
                        <div className="list-group">
                          {selectedSource.templateFiles.map((template, idx) => (
                            <div key={idx} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-1">
                                    <i className={`bi ${template.type === 'excel' ? 'bi-file-earmark-excel' : 'bi-filetype-csv'} me-2 text-success`}></i>
                                    {template.name}
                                  </h6>
                                  <p className="mb-0 small text-muted">{template.description}</p>
                                  {template.size && <span className="badge bg-secondary mt-1">{template.size}</span>}
                                </div>
                                <button 
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    const isExcel = template.type === 'excel';
                                    if (!isExcel) {
                                      const sampleData = getSampleDataByTemplate(template.name);
                                      if (sampleData) {
                                        downloadCSV(template.name, sampleData);
                                      } else {
                                        window.alert('Template chưa có dữ liệu mẫu');
                                      }
                                    } else {
                                      window.alert('Excel template sẽ được triển khai trong phiên bản tới');
                                    }
                                  }}
                                >
                                  <i className="bi bi-download me-1"></i>
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    <div className="mt-4 text-center">
                      <a href="/data-upload" className="btn btn-success">
                        <i className="bi bi-cloud-upload me-2"></i>
                        Đi đến trang Upload
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
              <>
              {/* Tables List */}
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-header bg-white">
                  <h5 className="mb-0">
                    <i className="bi bi-table me-2"></i>
                    {t('dataRepository.tableView')} - {selectedSource.displayName}
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th>{t('indicators.fieldMapping')}</th>
                          <th className="text-center">Columns</th>
                          <th className="text-end">Rows</th>
                          <th>{t('dataRepository.fileName')}</th>
                          <th className="text-center">{t('common.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedSource.tables.map((table, index) => (
                          <tr 
                            key={index}
                            className={selectedTable?.id === table.id ? 'table-active' : ''}
                          >
                            <td className="text-center text-muted">{index + 1}</td>
                            <td>
                              <strong>{table.displayName}</strong>
                              <div className="small text-muted">
                                <code>{table.id}</code>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-secondary">{table.columnCount}</span>
                            </td>
                            <td className="text-end">
                              <strong>{table.rowCount.toLocaleString()}</strong>
                            </td>
                            <td>
                              <small className="text-muted">{table.sourceFile}</small>
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => handleViewTable(table)}
                                title={t('dataRepository.viewData')}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => handleEditTable(table)}
                                title={t('common.edit')}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Table Data Preview */}
              {selectedTable && (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-gradient-info text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                      {selectedTable.displayName}
                    </h5>
                    <div>
                      <button
                        className="btn btn-sm btn-light me-2"
                        onClick={() => handleEditTable(selectedTable)}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        {t('common.edit')}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-light"
                        onClick={() => setSelectedTable(null)}
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {/* Schema Info */}
                    <div className="p-3 bg-light border-bottom">
                      <h6 className="mb-2">
                        <i className="bi bi-diagram-3 me-1"></i>
                        Schema
                      </h6>
                      <div className="row g-2">
                        {selectedTable.headers.map((header, index) => {
                          const sampleValue = selectedTable.data[0]?.[header];
                          const fieldType = inferFieldType(sampleValue);
                          const typeIcon = {
                            'number': 'bi-123',
                            'text': 'bi-fonts',
                            'date': 'bi-calendar-date',
                            'boolean': 'bi-toggle-on'
                          }[fieldType] || 'bi-question-circle';
                          
                          return (
                            <div key={index} className="col-md-3">
                              <div className="small p-2 bg-white border rounded">
                                <i className={`bi ${typeIcon} me-1 text-primary`}></i>
                                <strong>{header}</strong>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  {fieldType}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Data Table */}
                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <table className="table table-sm table-bordered table-hover mb-0">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th className="text-center" style={{ width: '50px' }}>#</th>
                            {selectedTable.headers.map((header, index) => (
                              <th key={index}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTable.data.slice(0, 100).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              <td className="text-center bg-light fw-bold text-muted">
                                {rowIndex + 1}
                              </td>
                              {selectedTable.headers.map((header, colIndex) => {
                                const value = row[header];
                                const fieldType = inferFieldType(value);
                                return (
                                  <td 
                                    key={colIndex}
                                    className={fieldType === 'number' ? 'text-end' : ''}
                                  >
                                    {value !== null && value !== undefined ? String(value) : '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {selectedTable.data.length > 100 && (
                      <div className="alert alert-info m-3">
                        <i className="bi bi-info-circle me-2"></i>
                        {t('dataRepository.showingFirst100', { total: selectedTable.data.length })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal with Simple Spreadsheet */}
      {showEditModal && editingData && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={(e) => {
            if (e.target.className.includes('modal')) {
              setShowEditModal(false);
              setEditingData(null);
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-fullscreen-lg-down">
            <div className="modal-content">
              <div className="modal-header bg-gradient-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  {t('dataRepository.editDataTitle', { name: editingData.table.displayName })}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingData(null);
                  }}
                ></button>
              </div>
              <div className="modal-body p-0">
                <div className="alert alert-info m-3 mb-0 border-0">
                  <div className="d-flex align-items-start">
                    <i className="bi bi-lightbulb me-2 mt-1"></i>
                    <div className="small">
                      <strong>{t('dataRepository.editDataDesc')}</strong>
                      <ul className="mb-0 mt-1">
                        <li>{t('dataRepository.editInstructions.tab')}</li>
                        <li>{t('dataRepository.editInstructions.enter')}</li>
                        <li>{t('dataRepository.editInstructions.type')}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div style={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'auto' }} className="p-3">
                  <table className="table table-sm table-bordered table-hover mb-0" style={{ minWidth: '800px' }}>
                    <thead className="table-light sticky-top" style={{ top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="text-center bg-primary text-white" style={{ width: '60px', minWidth: '60px' }}>#</th>
                        {editingData.headers.map((header, index) => (
                          <th key={index} className="bg-light" style={{ minWidth: '150px', fontWeight: '600' }}>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-grip-vertical text-muted me-2" style={{ cursor: 'grab' }}></i>
                              {header}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {editingData.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="align-middle">
                          <td className="text-center bg-light fw-bold text-muted" style={{ fontSize: '0.85rem' }}>
                            {rowIndex + 1}
                          </td>
                          {editingData.headers.map((header, colIndex) => (
                            <td key={colIndex} className="p-0">
                              <input
                                type="text"
                                className="form-control form-control-sm border-0 rounded-0"
                                value={row[header] ?? ''}
                                onChange={(e) => {
                                  const newData = [...editingData.data];
                                  newData[rowIndex][header] = e.target.value;
                                  setEditingData({ ...editingData, data: newData });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    // Move to next row, same column
                                    const nextRow = rowIndex + 1;
                                    if (nextRow < editingData.data.length) {
                                      const nextInput = e.target.closest('tbody').children[nextRow].children[colIndex + 1].querySelector('input');
                                      if (nextInput) nextInput.focus();
                                    }
                                  } else if (e.key === 'Tab' && !e.shiftKey) {
                                    e.preventDefault();
                                    // Move to next column
                                    const nextCol = colIndex + 1;
                                    if (nextCol < editingData.headers.length) {
                                      const nextInput = e.target.closest('tr').children[nextCol + 1].querySelector('input');
                                      if (nextInput) nextInput.focus();
                                    } else if (rowIndex + 1 < editingData.data.length) {
                                      // Move to first column of next row
                                      const nextInput = e.target.closest('tbody').children[rowIndex + 1].children[1].querySelector('input');
                                      if (nextInput) nextInput.focus();
                                    }
                                  }
                                }}
                                style={{ 
                                  minWidth: '150px',
                                  padding: '0.5rem',
                                  fontSize: '0.875rem'
                                }}
                                placeholder={t('dataRepository.enterValue')}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-top p-3 bg-light">
                  <div className="row text-center">
                    <div className="col-md-3">
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-table text-primary me-2 fs-5"></i>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>{t('dataRepository.totalRows')}</small>
                          <div className="fw-bold text-primary">{editingData.data.length}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-columns text-primary me-2 fs-5"></i>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>{t('dataRepository.totalColumns')}</small>
                          <div className="fw-bold text-primary">{editingData.headers.length}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="alert alert-warning mb-0 py-2 d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <small>{t('dataRepository.saveNotice')}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingData(null);
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveEdit}
                >
                  <i className="bi bi-save me-2"></i>
                  {t('dataRepository.saveChanges')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataRepository;
